import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '@/store';
import {
  fetchBatteryStatusFromReport,
  fetchMonitoredValuesFromReport,
  fetchProgrammedParametersFromReport
} from '@/store/selectors/transmissionReportSelectors';

import { MonitoredValuesForm } from '@/pages/physician/TransmissionReportPage/utils/types';
import { ReportLocationEnum, UpdateNoteParams } from '@/types/alert';
import { Counters } from '@/types/counters';
import {
  ReportInClinicEpisode,
  ReportPresentingCard,
  SignatureStatus,
  TransmissionReport
} from '@/types/transmissionReport';
import {
  PRACTITIONER_SIGNATURE_ROLES,
  PRACTITIONER_SIGNATURE_ROLES_TEMP,
  UserRole
} from '@/types/user';
import { DeviceLeadLocation, UpdateDeviceLeadDto } from '@/types/vendorTransmissionReport';

interface BatteryStatusForm {
  voltage: string;
  longevity: string;
  percentRemaining: string;
  chargeTime: string;
  batteryMeasurementTime: string;
  batteryStatus: string;
}

export interface ProgrammedParametersForm {
  mode?: string | null;
  lowerRate?: string | null;
  upperRate?: string | null;
  atOrAf?: string | null;
  vtZone1?: string | null;
  vtZone2?: string | null;
  vf?: string | null;
  pvarp?: string | null;
  pvab?: string | null;
}

interface TransmissionReportFormState {
  reportId: number | null;
  isInitialized: boolean;
  isActiveReportSigned: boolean;
  isActiveReportFinalSigned: boolean;
  batteryStatus: BatteryStatusForm;
  monitoredValues: MonitoredValuesForm;
  inClinicEpisode: ReportInClinicEpisode;
  inClinicPresenting: ReportPresentingCard;
  notes: UpdateNoteParams[];
  programmedParameters: ProgrammedParametersForm;
  counters: Counters;
  isEditingSignedReport: boolean;
  comment: string | undefined;
  programmingChanges: string | null;
  emrEncounterId: string | null;
  otherReason: string | undefined;
  selectedVendors: string[];
  isCombineReportsEnabled: boolean;
  activeTab: 'vendor' | 'rhythm';
  // PDF page navigation shared state
  vendorPdfNumPages: number;
  vendorPdfScrollTarget: number | null;
  dirty: {
    batteryStatus: boolean;
    monitoredValues: boolean;
    inClinicEpisode: boolean;
    inClinicPresenting: boolean;
    notes: boolean;
    programmedParameters: boolean;
    counters: boolean;
    comment: boolean;
    otherReason: boolean;
    programmingChanges: boolean;
    emrEncounterId: boolean;
  };
  lastSavedAt: string | null;
  isSaving: boolean;
  persistenceMetadata: {
    lastSaveAttempt: number | null;
    version: number;
  };
}

const emptyBatteryStatus: BatteryStatusForm = {
  voltage: '',
  longevity: '',
  percentRemaining: '',
  chargeTime: '',
  batteryMeasurementTime: '',
  batteryStatus: ''
};

const emptyMonitoredValues: MonitoredValuesForm = {
  leadIds: {
    [DeviceLeadLocation.RA]: '',
    [DeviceLeadLocation.RV]: '',
    [DeviceLeadLocation.LV]: ''
  },
  sensing: {
    [DeviceLeadLocation.RA]: '',
    [DeviceLeadLocation.RV]: '',
    [DeviceLeadLocation.LV]: ''
  },
  impedance: {
    [DeviceLeadLocation.RA]: '',
    [DeviceLeadLocation.RV]: '',
    [DeviceLeadLocation.LV]: ''
  },
  threshold: {
    [DeviceLeadLocation.RA]: '',
    [DeviceLeadLocation.RV]: '',
    [DeviceLeadLocation.LV]: ''
  },
  pulsewidth: {
    [DeviceLeadLocation.RA]: '',
    [DeviceLeadLocation.RV]: '',
    [DeviceLeadLocation.LV]: ''
  },
  highVoltageImpedance: {
    [DeviceLeadLocation.RA]: '',
    [DeviceLeadLocation.RV]: '',
    [DeviceLeadLocation.LV]: ''
  },
  svcImpedance: {
    [DeviceLeadLocation.RA]: '',
    [DeviceLeadLocation.RV]: '',
    [DeviceLeadLocation.LV]: ''
  },
  percentPaced: {
    [DeviceLeadLocation.RA]: '',
    [DeviceLeadLocation.RV]: '',
    [DeviceLeadLocation.LV]: ''
  },
  afBurden: {
    [DeviceLeadLocation.RA]: '',
    [DeviceLeadLocation.RV]: '',
    [DeviceLeadLocation.LV]: ''
  }
};

const emptyCounters = {
  Id: '',
  symptomCounters: '',
  symptomTable: '',
  tachyCounters: '',
  tachyTime: '',
  pauseCounters: '',
  pauseTime: '',
  bradyCounters: '',
  bradyTime: '',
  ATCounters: '',
  ATTime: '',
  AFCounters: '',
  AFTime: '',
  TimeInATAFCounters: '',
  TimeInATAFTime: '',
  createdAt: '',
  updatedAt: ''
};

const emptyProgrammedParameters = {
  id: '',
  mode: '',
  lowerRate: '',
  upperRate: '',
  atOrAf: '',
  vtZone1: '',
  vtZone2: '',
  vf: ''
};

const emptyInClinicEpisode: ReportInClinicEpisode = {
  id: '',
  atrModeSwitch: '',
  maxTime: '',
  atrialBurden: '',
  vhrEpisodes: '',
  episodeComments: ''
};

const emptyInClinicPresenting: ReportPresentingCard = {
  presentingRhythm: '',
  presentingRate: '',
  underlyingRhythm: '',
  underlyingRate: ''
};

const initialState: TransmissionReportFormState = {
  reportId: null,
  isInitialized: false,
  isActiveReportSigned: false,
  isActiveReportFinalSigned: false,
  batteryStatus: emptyBatteryStatus,
  monitoredValues: emptyMonitoredValues,
  programmedParameters: emptyProgrammedParameters,
  counters: emptyCounters,
  inClinicEpisode: emptyInClinicEpisode,
  inClinicPresenting: emptyInClinicPresenting,
  isEditingSignedReport: false,
  comment: '',
  otherReason: '',
  selectedVendors: [],
  programmingChanges: '',
  emrEncounterId: '',
  isCombineReportsEnabled: false,
  activeTab: 'vendor',
  vendorPdfNumPages: 0,
  vendorPdfScrollTarget: null,
  dirty: {
    batteryStatus: false,
    monitoredValues: false,
    inClinicEpisode: false,
    inClinicPresenting: false,
    notes: false,
    programmedParameters: false,
    counters: false,
    comment: false,
    otherReason: false,
    programmingChanges: false,
    emrEncounterId: false
  },
  notes: [],
  lastSavedAt: null,
  isSaving: false,
  persistenceMetadata: {
    lastSaveAttempt: null,
    version: 1
  }
};

const transmissionReportFormSlice = createSlice({
  name: 'transmissionReportForm',
  initialState,
  reducers: {
    initializeForm: (
      state,
      action: PayloadAction<{
        transmissionReport: TransmissionReport;
        userRole: UserRole | undefined;
        forceReplace?: boolean;
      }>
    ) => {
      const { transmissionReport, userRole, forceReplace = false } = action.payload;

      const hasDirtyData = Object.values(state.dirty).some((isDirty) => isDirty);
      const isSameReport = state.reportId === transmissionReport.id;
      const isDifferentReport = state.reportId !== transmissionReport.id;

      if (hasDirtyData && isSameReport && !forceReplace) {
        const signStatus = transmissionReport.signatureStatus;
        state.isActiveReportSigned = false;
        state.isActiveReportFinalSigned = false;
        if (
          signStatus === SignatureStatus.Signed ||
          signStatus === SignatureStatus.SignatureNotRequired
        ) {
          state.isActiveReportSigned = true;
          state.isActiveReportFinalSigned = true;
        } else if (
          signStatus === SignatureStatus.NonPractitionerSigned ||
          signStatus === SignatureStatus.PendingReview
        ) {
          if (
            userRole &&
            (signStatus === SignatureStatus.PendingReview
              ? !PRACTITIONER_SIGNATURE_ROLES_TEMP.includes(userRole)
              : !PRACTITIONER_SIGNATURE_ROLES.includes(userRole))
          ) {
            state.isActiveReportSigned = true;
          }
        }
        state.isInitialized = true;
        return;
      }

      if (isDifferentReport) {
        const currentSelectedVendors = state.selectedVendors;
        const defaultCombineSetting = transmissionReport?.account?.saveCombinedPDF ?? false;
        Object.assign(state, {
          ...initialState,
          selectedVendors: currentSelectedVendors,
          isCombineReportsEnabled: defaultCombineSetting,
          persistenceMetadata: {
            ...state.persistenceMetadata,
            version: state.persistenceMetadata.version + 1
          }
        });
      }

      state.reportId = transmissionReport.id;
      state.comment = transmissionReport.comment;
      state.otherReason = transmissionReport.otherReason;
      state.programmingChanges = transmissionReport.programmingChanges;
      state.emrEncounterId = transmissionReport.emrEncounterId;
      state.isCombineReportsEnabled = transmissionReport?.account?.saveCombinedPDF ?? false;

      const batteryStatus = fetchBatteryStatusFromReport(transmissionReport);
      const monitoredValues = fetchMonitoredValuesFromReport(transmissionReport);
      const programmedParameters = fetchProgrammedParametersFromReport(transmissionReport);
      const counters = transmissionReport.counters ?? emptyCounters;
      const inClinicEpisode = transmissionReport.inClinicEpisode ?? emptyInClinicEpisode;
      const inClinicPresenting = transmissionReport.presentingCard ?? emptyInClinicPresenting;

      if (batteryStatus) state.batteryStatus = batteryStatus;
      if (monitoredValues) state.monitoredValues = monitoredValues;
      if (programmedParameters) state.programmedParameters = programmedParameters;
      if (counters) state.counters = counters;
      if (inClinicEpisode) state.inClinicEpisode = inClinicEpisode;
      if (inClinicPresenting) state.inClinicPresenting = inClinicPresenting;

      if (transmissionReport.notes && transmissionReport.notes.length > 0) {
        state.notes = transmissionReport.notes.map((note) => ({
          note: note.note,
          location: note.location,
          updatedByUser: note.updatedByUser,
          updatedAt: note.updatedAt
        }));
      }

      state.isInitialized = true;
      state.dirty = initialState.dirty;

      if (isDifferentReport) {
        state.lastSavedAt = null;
        state.isSaving = false;
      }

      const signStatus = transmissionReport.signatureStatus;
      state.isActiveReportSigned = false;
      state.isActiveReportFinalSigned = false;
      if (
        signStatus === SignatureStatus.Signed ||
        signStatus === SignatureStatus.SignatureNotRequired
      ) {
        state.isActiveReportSigned = true;
        state.isActiveReportFinalSigned = true;
      } else if (
        signStatus === SignatureStatus.NonPractitionerSigned ||
        signStatus === SignatureStatus.PendingReview
      ) {
        if (
          userRole &&
          (signStatus === SignatureStatus.PendingReview
            ? !PRACTITIONER_SIGNATURE_ROLES_TEMP.includes(userRole)
            : !PRACTITIONER_SIGNATURE_ROLES.includes(userRole))
        ) {
          state.isActiveReportSigned = true;
        }
      }

      state.persistenceMetadata.version += 1;
    },

    forceRefreshForm: (
      state,
      action: PayloadAction<{
        transmissionReport: TransmissionReport;
        userRole: UserRole | undefined;
      }>
    ) => {
      transmissionReportFormSlice.caseReducers.initializeForm(state, {
        ...action,
        payload: { ...action.payload, forceReplace: true }
      });
    },

    resetForm: (state) => {
      const preserveCombineSetting = state.isCombineReportsEnabled;
      Object.assign(state, {
        ...initialState,
        activeTab: 'vendor',
        isCombineReportsEnabled: preserveCombineSetting,
        persistenceMetadata: {
          ...initialState.persistenceMetadata,
          version: state.persistenceMetadata.version + 1
        }
      });
    },

    clearPersistedData: (state) => {
      Object.assign(state, {
        ...initialState,
        persistenceMetadata: { lastSaveAttempt: null, version: 1 }
      });
    },

    clearPersistedDataForReport: (state, action: PayloadAction<number>) => {
      const targetReportId = action.payload;
      if (state.reportId === targetReportId) {
        Object.assign(state, {
          ...initialState,
          persistenceMetadata: {
            lastSaveAttempt: null,
            version: state.persistenceMetadata.version + 1
          }
        });
      }
    },

    resetDirty: (state) => {
      state.dirty = initialState.dirty;
      state.persistenceMetadata.lastSaveAttempt = Date.now();
    },

    resetDirtyFields: (
      state,
      action: PayloadAction<Partial<Record<keyof typeof initialState.dirty, boolean>>>
    ) => {
      const savedFields = action.payload;
      (Object.keys(savedFields) as Array<keyof typeof initialState.dirty>).forEach((key) => {
        if (savedFields[key]) state.dirty[key] = false;
      });
      state.persistenceMetadata.lastSaveAttempt = Date.now();
    },

    setSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
    },

    setLastSavedAt: (state, action: PayloadAction<string>) => {
      state.lastSavedAt = action.payload;
      state.isSaving = false;
    },

    markSaveAttempt: (state) => {
      state.persistenceMetadata.lastSaveAttempt = Date.now();
    },

    // --- PDF page navigation ---
    setVendorPdfNumPages: (state, action: PayloadAction<number>) => {
      state.vendorPdfNumPages = action.payload;
    },
    setVendorPdfScrollTarget: (state, action: PayloadAction<number | null>) => {
      state.vendorPdfScrollTarget = action.payload;
    },

    updateBatteryField: (
      state,
      action: PayloadAction<{ field: keyof BatteryStatusForm; value: string }>
    ) => {
      const { field, value } = action.payload;
      state.batteryStatus[field] = value;
      state.dirty.batteryStatus = true;
    },

    updateMonitoredValuesField: (
      state,
      action: PayloadAction<{
        field: keyof MonitoredValuesForm;
        chamber: DeviceLeadLocation;
        value: string | null | undefined;
      }>
    ) => {
      const { field, value, chamber } = action.payload;
      state.monitoredValues[field] = { ...state.monitoredValues[field], [chamber]: value };
      state.dirty.monitoredValues = true;
    },

    updateProgrammedParametersField: (
      state,
      action: PayloadAction<{ field: keyof ProgrammedParametersForm; value: string | null | undefined }>
    ) => {
      const { field, value } = action.payload;
      state.programmedParameters[field] = value;
      state.dirty.programmedParameters = true;
    },

    updateCountersField: (
      state,
      action: PayloadAction<{ field: keyof Counters; value: string | null }>
    ) => {
      const { field, value } = action.payload;
      if (field === 'Id') return;
      state.counters[field] = value;
      state.dirty.counters = true;
    },

    updateInClinicEpisodeField: (
      state,
      action: PayloadAction<{ field: keyof ReportInClinicEpisode; value: string }>
    ) => {
      const { field, value } = action.payload;
      if (field === 'id') return;
      state.inClinicEpisode[field] = value;
      state.dirty.inClinicEpisode = true;
    },

    updateInClinicPresentingField: (
      state,
      action: PayloadAction<{ field: keyof ReportPresentingCard; value: string }>
    ) => {
      const { field, value } = action.payload;
      state.inClinicPresenting[field] = value;
      state.dirty.inClinicPresenting = true;
    },

    setActiveReportSigned: (state, action: PayloadAction<boolean>) => {
      state.isActiveReportSigned = action.payload;
    },

    setActiveReportFinalSigned: (state, action: PayloadAction<boolean>) => {
      state.isActiveReportFinalSigned = action.payload;
    },

    setCombineReportsEnabled: (state, action: PayloadAction<boolean>) => {
      state.isCombineReportsEnabled = action.payload;
    },

    setActiveTab: (state, action: PayloadAction<'vendor' | 'rhythm'>) => {
      state.activeTab = action.payload;
    },

    setNoteValue: (state, action: PayloadAction<UpdateNoteParams>) => {
      const { note, location, alertId } = action.payload;
      let existingNoteIndex = -1;

      if (location === ReportLocationEnum.Alert) {
        existingNoteIndex = state.notes.findIndex(
          (n) => n.alertId === alertId && n.location === location
        );
      } else {
        existingNoteIndex = state.notes.findIndex((n) => n.location === location);
      }

      if (existingNoteIndex !== -1) {
        state.notes[existingNoteIndex].note = note;
      } else {
        const newNote: UpdateNoteParams = { note, location };
        if (location === ReportLocationEnum.Alert) newNote.alertId = alertId;
        state.notes.push(newNote);
      }

      if (note && note.trim() !== '') state.dirty.notes = true;
    },

    setIsEditingSignedReport: (state, action: PayloadAction<boolean>) => {
      state.isEditingSignedReport = action.payload;
    },
    setPhysicianComment: (state, action: PayloadAction<string>) => {
      state.comment = action.payload;
      state.dirty.comment = true;
    },
    setFollowUpReason: (state, action: PayloadAction<string>) => {
      state.otherReason = action.payload;
      state.dirty.otherReason = true;
    },
    setSelectedVendors: (state, action: PayloadAction<string[]>) => {
      state.selectedVendors = action.payload;
    },
    setProgrammingChanges: (state, action: PayloadAction<string>) => {
      state.programmingChanges = action.payload;
      state.dirty.programmingChanges = true;
    },
    setEmrEncounterId: (state, action: PayloadAction<string>) => {
      state.emrEncounterId = action.payload;
      state.dirty.emrEncounterId = true;
    },
    toggleVendor: (state, action: PayloadAction<string>) => {
      const vendor = action.payload;
      if (state.selectedVendors.includes(vendor)) {
        state.selectedVendors = state.selectedVendors.filter((v) => v !== vendor);
      } else {
        state.selectedVendors = [...state.selectedVendors, vendor];
      }
    },
    toggleAllVendors: (state, action: PayloadAction<string[]>) => {
      const allVendors = action.payload;
      if (state.selectedVendors.length === allVendors.length) {
        state.selectedVendors = [];
      } else {
        state.selectedVendors = allVendors;
      }
    }
  }
});

export const {
  initializeForm,
  forceRefreshForm,
  resetForm,
  clearPersistedData,
  clearPersistedDataForReport,
  resetDirty,
  resetDirtyFields,
  setSaving,
  setLastSavedAt,
  markSaveAttempt,
  setVendorPdfNumPages,
  setVendorPdfScrollTarget,
  updateBatteryField,
  updateMonitoredValuesField,
  updateInClinicEpisodeField,
  updateInClinicPresentingField,
  setActiveReportSigned,
  setNoteValue,
  updateProgrammedParametersField,
  updateCountersField,
  setIsEditingSignedReport,
  setCombineReportsEnabled,
  setActiveTab,
  setPhysicianComment,
  setProgrammingChanges,
  setEmrEncounterId,
  setFollowUpReason,
  setSelectedVendors,
  toggleVendor,
  toggleAllVendors
} = transmissionReportFormSlice.actions;

export default transmissionReportFormSlice.reducer;

export const selectIsAnyFormDirty = (state: RootState) =>
  Object.values(state.transmissionReportForm.dirty).some((isDirty) => isDirty);

export const selectAllFormData = (state: RootState) => {
  const {
    batteryStatus, monitoredValues, inClinicEpisode, inClinicPresenting,
    notes, programmedParameters, counters, comment, programmingChanges,
    emrEncounterId, otherReason
  } = state.transmissionReportForm;
  return {
    batteryStatus, monitoredValues, inClinicEpisode, inClinicPresenting,
    notes, programmedParameters, counters, comment, programmingChanges,
    emrEncounterId, otherReason
  };
};

export const selectLastSavedAt = (state: RootState) =>
  state.transmissionReportForm.lastSavedAt;

export const selectIsSaving = (state: RootState) =>
  state.transmissionReportForm.isSaving;

export const selectBatteryStatusForm = (state: RootState) =>
  state.transmissionReportForm.batteryStatus;

export const selectMonitoredValuesForm = (state: RootState) =>
  state.transmissionReportForm.monitoredValues;

export const selectIsBatteryStatusDirty = (state: RootState) =>
  state.transmissionReportForm.dirty.batteryStatus;

export const selectIsMonitoredValuesDirty = (state: RootState) =>
  state.transmissionReportForm.dirty.monitoredValues;

export const selectProgrammedParametersForm = (state: RootState) =>
  state.transmissionReportForm.programmedParameters;

export const selectIsProgrammedParametersDirty = (state: RootState) =>
  state.transmissionReportForm.dirty.programmedParameters;

export const selectFormReportId = (state: RootState) =>
  state.transmissionReportForm.reportId;

export const selectFormPersistenceMetadata = (state: RootState) =>
  state.transmissionReportForm.persistenceMetadata;

export const selectHasUnsavedChanges = (state: RootState) => {
  const form = state.transmissionReportForm;
  return form.isInitialized && Object.values(form.dirty).some((isDirty) => isDirty);
};

export const selectIsCombineReportsEnabled = (state: RootState) =>
  state.transmissionReportForm.isCombineReportsEnabled;

export const selectActiveTab = (state: RootState) =>
  state.transmissionReportForm.activeTab;

export const selectVendorPdfNumPages = (state: RootState) =>
  state.transmissionReportForm.vendorPdfNumPages;

export const selectVendorPdfScrollTarget = (state: RootState) =>
  state.transmissionReportForm.vendorPdfScrollTarget;

export const formToApiPayload = (
  formData: {
    batteryStatus?: BatteryStatusForm;
    monitoredValues?: MonitoredValuesForm;
    inClinicEpisode?: ReportInClinicEpisode;
    inClinicPresenting?: ReportPresentingCard;
    notes?: UpdateNoteParams[];
    programmingChanges?: string | null;
    emrEncounterId?: string | null;
    programmedParameters?: ProgrammedParametersForm;
    counters?: Counters;
    comment?: string;
    otherReason?: string;
    [key: string]: any;
  },
  dirties: { [key: string]: boolean }
) => {
  const payload: Record<string, any> = {};

  if (formData.batteryStatus && dirties.batteryStatus) {
    payload['device.batteryVoltage'] = formData.batteryStatus.voltage;
    payload['device.batteryRemainingLongevity'] = formData.batteryStatus.longevity;
    payload['device.batteryPercentRemaining'] = formData.batteryStatus.percentRemaining;
    payload['device.capacitorChargeTime'] = formData.batteryStatus.chargeTime;
    payload['device.batteryMeasurementTime'] = formData.batteryStatus.batteryMeasurementTime;
    payload['device.batteryStatus'] = formData.batteryStatus.batteryStatus;
  }

  if (formData.monitoredValues && dirties.monitoredValues) {
    const deviceLeads: Partial<UpdateDeviceLeadDto>[] = [];
    for (const chamber in DeviceLeadLocation) {
      const chamberKey = chamber as keyof typeof DeviceLeadLocation;
      const leadId = formData.monitoredValues.leadIds[chamberKey] ?? '';
      if (!leadId) continue;
      deviceLeads.push({
        deviceLeadId: parseInt(leadId),
        sensing: formData.monitoredValues.sensing[chamberKey],
        impedance: formData.monitoredValues.impedance[chamberKey],
        threshold: formData.monitoredValues.threshold[chamberKey],
        pulsewidth: formData.monitoredValues.pulsewidth[chamberKey],
        svcImpedance: formData.monitoredValues.svcImpedance[chamberKey]
      });
    }
    payload['deviceLeads'] = deviceLeads;
    payload['deviceMeasurement.hvImpedance'] =
      formData.monitoredValues.highVoltageImpedance[DeviceLeadLocation.RV] || null;
    payload['deviceMeasurement.apAPacePercent'] =
      formData.monitoredValues.percentPaced[DeviceLeadLocation.RA] || null;
    payload['deviceMeasurement.rvpRvPacePercent'] =
      formData.monitoredValues.percentPaced[DeviceLeadLocation.RV] || null;
    payload['deviceMeasurement.lvpPercent'] =
      formData.monitoredValues.percentPaced[DeviceLeadLocation.LV] || null;
    payload['deviceMeasurement.afBurden'] =
      formData.monitoredValues.afBurden[DeviceLeadLocation.RA] || null;
  }

  const notes =
    formData.notes && formData.notes.length > 0 && dirties.notes ? formData.notes : null;
  const programmedParameters =
    formData.programmedParameters && dirties.programmedParameters
      ? formData.programmedParameters : null;
  const inClinicEpisode =
    formData.inClinicEpisode && dirties.inClinicEpisode ? formData.inClinicEpisode : null;
  const inClinicPresenting =
    formData.inClinicPresenting && dirties.inClinicPresenting ? formData.inClinicPresenting : null;
  const counters = formData.counters;

  if (formData.counters && dirties.counters) {
    payload['deviceCounters.AFCounters'] = formData.counters.AFCounters || null;
    payload['deviceCounters.AFTime'] = formData.counters.AFTime || null;
    payload['deviceCounters.ATCounters'] = formData.counters.ATCounters || null;
    payload['deviceCounters.ATTime'] = formData.counters.ATTime || null;
    payload['deviceCounters.TimeInATAFCounters'] = formData.counters.TimeInATAFCounters || null;
    payload['deviceCounters.TimeInATAFTime'] = formData.counters.TimeInATAFTime || null;
    payload['deviceCounters.bradyCounters'] = formData.counters.bradyCounters || null;
    payload['deviceCounters.bradyTime'] = formData.counters.bradyTime || null;
    payload['deviceCounters.pauseCounters'] = formData.counters.pauseCounters || null;
    payload['deviceCounters.pauseTime'] = formData.counters.pauseTime || null;
    payload['deviceCounters.symptomCounters'] = formData.counters.symptomCounters || null;
    payload['deviceCounters.symptomTable'] = formData.counters.symptomTable || null;
    payload['deviceCounters.tachyCounters'] = formData.counters.tachyCounters || null;
    payload['deviceCounters.tachyTime'] = formData.counters.tachyTime || null;
  }

  return {
    overrides: payload,
    notes,
    inClinicEpisode,
    inClinicPresenting,
    programmedParameters,
    counters,
    comment: formData.comment,
    otherReason: formData.otherReason,
    programmingChanges: formData.programmingChanges,
    emrEncounterId: formData.emrEncounterId
  };
};